import { OAuth2Token } from "../models/login-model";
import { ensureConfig } from "./configuration-service";

const SMOKEBALL_TOKEN_STORAGE_KEY = "SmokeballToken";
/**
 * The query parameters that will be sent to the /authorization endpoint
 * for the authorization_code request.
 */
interface LoginRequest {
  state?: string;
  scope?: string;
  client_id: string;
  redirect_uri: string;
  response_type: "code";
  code_challenge?: string;
  [key: string]: string | undefined;
  code_challenge_method?: "plain" | "S256";
}

/**
 * Response from the /token endpoint.
 */
export interface TokenResponse {
  response: Response; // TODO: Instead of returning the whole Fetch response just return errors (or no errors) in a defined format
  token: OAuth2Token | null;
}

/**
 * Response body from the /token endpoint.
 */
interface TokenResponseBody {
  access_token: string;
  id_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
  scope?: string;
}

/**
 * The refresh_token request body to be sent to the /token endpoint.
 */
interface RefreshRequest extends Record<string, string> {
  client_id: string;
  refresh_token: string;
  grant_type: "refresh_token";
}

/**
 * The authorization_code request body to be sent to the /token endpoint.
 */
interface AuthorizationCodeRequest extends Record<string, string | undefined> {
  code: string;
  client_id: string;
  redirect_uri: string;
  grant_type: "authorization_code";
  code_verifier?: string;
}

export enum AuthenticationStatus {
  LoggedIn = "LoggedIn",
  LoggedOut = "LoggedOut",
  LoginInProgress = "LoginInProgress",
}

/**
 * Initiate login via the Cognito Hosted UI.
 */
export async function loginWithRedirect(cognitoUrl: string, clientId: string, redirectUrl: string) {
  console.error("loginWithRedirect...");
  const codeVerifier = generateCodeVerifier(100);
  window.sessionStorage.setItem("code_verifier", codeVerifier);
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  const request: LoginRequest = {
    scope: "openid",
    client_id: clientId,
    response_type: "code",
    redirect_uri: redirectUrl,
    code_challenge_method: "S256",
    code_challenge: codeChallenge,
  };

  const url = `${cognitoUrl}/oauth2/authorize?${generateQueryString(request)}`;

  // Redirect to the Cognito Hosted UI login page.
  window.location.href = url;
}

/**
 * Handle the redirect from the Cognito Hosted UI. This function must
 * be called within the same window as the initial call to loginWithRedirect()
 * @param code The authorisation code returned from Cognito
 * @returns
 */
export async function handleLoginRedirect(
  cognitoUrl: string,
  clientId: string,
  redirectUrl: string,
  code: string
): Promise<TokenResponse> {
  const authorisationCodeBody: AuthorizationCodeRequest = {
    code: code,
    client_id: clientId,
    redirect_uri: redirectUrl,
    grant_type: "authorization_code",
    code_verifier: window.sessionStorage.getItem("code_verifier") ?? undefined,
  };

  return await tokenRequest(cognitoUrl, authorisationCodeBody);
}

export async function getAuthStatus(): Promise<AuthenticationStatus> {
  const regionConfig = ensureConfig();

  const cognitoUrl = regionConfig.CognitoUrl;
  const clientId = regionConfig.CognitoClientId;
  const token = await getTokenFromStorage(cognitoUrl, clientId);
  const idToken = token?.idtoken;
  const hasIdToken = idToken !== undefined;
  const hasAccessToken = token?.accessToken !== undefined;
  const hasRefreshToken = token?.refreshToken !== undefined;

  if (hasIdToken && hasAccessToken) {
    if (hasTokenExpired(token)) {
      console.warn("Token has expired");
      return AuthenticationStatus.LoggedOut;
    }

    return AuthenticationStatus.LoggedIn;
  }

  console.warn(
    `Missing token: Access Token: ${hasAccessToken.toString()}. ID Token: ${hasIdToken.toString()}. Refresh Token: ${hasRefreshToken.toString()}`
  );
  return AuthenticationStatus.LoggedOut;
}

export const loadTokenFromBrowser = (): string | null => {
  const loaded =
    sessionStorage.getItem(SMOKEBALL_TOKEN_STORAGE_KEY) ?? localStorage.getItem(SMOKEBALL_TOKEN_STORAGE_KEY);
  return loaded ?? null;
};

/*
 * Because these services can be used in taskpane or dialogs sometimes we want
 * to use session storage and other times local storage.
 *
 * Dialogs should use session storage.
 *
 * Loading in any env should prefer session.
 *
 * If the token is cleared it will clear both session and local storages.
 *
 */

export const storeTokenInBrowser = (token: string | null, useSession?: boolean) => {
  if (token) {
    const storage = useSession ? sessionStorage : localStorage;
    storage.setItem(SMOKEBALL_TOKEN_STORAGE_KEY, token);
  } else {
    localStorage.removeItem(SMOKEBALL_TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(SMOKEBALL_TOKEN_STORAGE_KEY);
  }
};

export const setTokenToStorage = (token?: OAuth2Token): boolean => {
  if (!token || hasTokenExpired(token)) {
    storeTokenInBrowser(null);
    return false;
  }

  const tokenStr = JSON.stringify(token);
  storeTokenInBrowser(tokenStr);

  return true;
};

// Get the previously stored token from local storage, refreshing it if
// it has expired.
export async function getTokenFromStorage(cognitoUrl: string, clientId: string): Promise<OAuth2Token | null> {
  const tokenString = loadTokenFromBrowser();

  if (tokenString) {
    const token = JSON.parse(tokenString) as OAuth2Token;

    if (!token.accessToken) {
      console.info(`The is no auth token. Login required.`);
      return null;
    }

    if (hasTokenExpired(token)) {
      if (!token.refreshToken) {
        console.warn(`The token has expired and there is no fresh token. Login required.`);
        return null;
      }

      const refreshRequest: RefreshRequest = {
        client_id: clientId,
        grant_type: "refresh_token",
        refresh_token: token.refreshToken,
      };

      console.info(`The token has expired. Request refresh...`);

      const tokenResponse = await tokenRequest(cognitoUrl, refreshRequest);

      if (tokenResponse.response.ok && tokenResponse.token) {
        tokenResponse.token.refreshToken = token.refreshToken;

        setTokenToStorage(tokenResponse.token);

        return tokenResponse.token;
      } else {
        return null;
      }
    } else {
      return token;
    }
  }

  return null;
}

export const getFirmIdAsync = async (): Promise<string | null> => {
  const customClaims = await getCustomTokenClaimsAsync();
  return customClaims ? (customClaims["smokeball:firmId"] as string) : null;
};

export const getUserInfoAsync = async (): Promise<{
  UserId: string | null;
  Email: string | null;
  AccountId: string | null;
}> => {
  try {
    const { CognitoUrl, CognitoClientId } = ensureConfig();
    const token = await getTokenFromStorage(CognitoUrl, CognitoClientId);
    const payloadJson = token?.idtoken.split(".")[1] ?? "";
    const payload = JSON.parse(atob(payloadJson)) as Record<string, string>;
    return {
      UserId: payload["custom:smokeball:userId"] ?? null,
      Email: payload.email,
      AccountId: payload["custom:smokeball:firmId"] ?? null,
    };
  } catch {
    return { UserId: null, Email: null, AccountId: null };
  }
};

async function getCustomTokenClaimsAsync() {
  try {
    const { CognitoUrl, CognitoClientId } = ensureConfig();
    const token = await getTokenFromStorage(CognitoUrl, CognitoClientId);

    const payloadJson = token?.idtoken.split(".")[1] ?? "";
    const payload = JSON.parse(atob(payloadJson)) as Record<string, unknown>;

    return Object.fromEntries(
      Object.entries(payload)
        .filter(([k]) => k.startsWith("custom:"))
        .map(([k, v]) => [k.replace(/^custom:/, ""), v])
    ) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Determines whether or not the provided token has expired.
 * @param token The OAuth2Token to check the expiry time on.
 * @returns True if the provided token has an expiry time and has expired, otherwise false.
 */
function hasTokenExpired(token: OAuth2Token | undefined): boolean {
  if (!token?.expiresAt) {
    return true;
  }

  return token.expiresAt <= Date.now();
}

/**
 * Performs a request to the /token endpoint using an AuthorizationCodeRequest or RefreshRequest body.
 *
 * @param tokenRequestBody The body of the request to use.
 * @returns A TokenResponse containing the Fetch response and an OAuth2Token if the request was successful.
 */
async function tokenRequest(
  cognitorUrl: string,
  tokenRequestBody: AuthorizationCodeRequest | RefreshRequest
): Promise<TokenResponse> {
  const url = `${cognitorUrl}/oauth2/token`;

  const headers: Record<string, string> = {
    "Content-Type": "application/x-www-form-urlencoded",
  };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: generateQueryString(tokenRequestBody),
  });

  let tokenResponse: TokenResponse;

  if (response.ok) {
    const tokenResponseBody: Promise<TokenResponseBody> = response.json() as Promise<TokenResponseBody>;
    const token: OAuth2Token = await tokenResponseToOAuth2Token(tokenResponseBody);

    tokenResponse = {
      token: token,
      response: response,
    };
  } else {
    tokenResponse = {
      token: null,
      response: response,
    };
  }

  return tokenResponse;
}

/**
 * Converts the JSON response body from the token endpoint to an OAuth2Token type.
 * @param resp A promise for the response body from the token endpoint.
 * @returns A promise for an OAuth2Token object from the response body.
 */
function tokenResponseToOAuth2Token(resp: Promise<TokenResponseBody>): Promise<OAuth2Token> {
  return resp.then((body) => ({
    idtoken: body.id_token,
    accessToken: body.access_token,
    refreshToken: body.refresh_token ?? null,
    expiresAt: body.expires_in ? Date.now() + body.expires_in * 1000 : null,
  }));
}

/**
 * Generates a random string to be used as the code verifier for PKCE.
 * @param length The length of the string to generate.
 * @returns a random string of the length specified.
 */
function generateCodeVerifier(length: number): string {
  let text = "";
  const possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

  for (let i = 0; i < length; i++) {
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  }

  return text;
}

/**
 * Generates the code challenge for PKCE. Gets a SHA-256 hash of the code
 * verifier and converts the result to a base64 encoded string.
 * @param codeVerifier The code verifier to hash.
 * @returns A valid code challenge for the provided code verifier.
 */
async function generateCodeChallenge(codeVerifier: string | undefined) {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(codeVerifier));

  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/=/g, "")
    .replace(/\+/g, "-")
    .replace(/\//g, "_");
}

/**
 * Converts the provided object to URL search params (treating it as key-value pairs).
 * @param params The values to add together as URL params.
 * @returns A query string made up of the provided params.
 */
function generateQueryString(params: Record<string, undefined | number | string>): string {
  return new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined)) as Record<string, string>
  ).toString();
}
